import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import { Button } from '@/components/common/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/common/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/common/Popover';
import { ScrollArea } from '@/components/common/ScrollArea';
import { cn } from '@/lib/cn';

export interface PhoneInputProps extends Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> {
  label?: string;
  error?: string;
  touched?: boolean;
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (countryCode: string | undefined) => void;
}

const DEFAULT_COUNTRY = 'LT' as RPNInput.Country;

const PhoneInput = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(
  (
    { className, onChange, value, label, error, touched, id, name, ...props },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [country, setCountry] = React.useState<RPNInput.Country>(() => {
      if (!value) return DEFAULT_COUNTRY;
      try {
        const parsed = RPNInput.parsePhoneNumber(value);
        return parsed?.country ?? DEFAULT_COUNTRY;
      } catch {
        return DEFAULT_COUNTRY;
      }
    });

    const handleCountryChange = React.useCallback(
      (newCountry: RPNInput.Country | undefined) => {
        const next = newCountry ?? DEFAULT_COUNTRY;
        setCountry(next);
        props.onCountryChange?.(next);
      },
      [props.onCountryChange],
    );

    return (
      <div className="group relative w-full">
        <div
          className={cn(
            'border-input ring-offset-background flex items-center rounded-[0.625rem] border bg-white transition-all duration-200',
            isFocused ? 'border-secondary border-[0.09375rem]' : '',
            touched && error ? 'border-destructive' : '',
            className,
          )}
        >
          <RPNInput.default
            ref={ref}
            className="flex w-full items-center"
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            inputComponent={InputComponent}
            smartCaret={false}
            defaultCountry={DEFAULT_COUNTRY}
            country={country}
            value={value || undefined}
            onCountryChange={handleCountryChange}
            onChange={(val) => onChange(val || '')}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            id={id}
            name={name}
            {...({ error, touched } as Record<string, unknown>)}
            {...props}
          />
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'bg-background text-muted-foreground pointer-events-none absolute -top-3 left-4 z-10 flex h-auto items-center px-1 text-xs transition-all duration-200 ease-out',
                touched && error ? 'text-destructive' : '',
              )}
            >
              {label}
              {touched && error && (
                <span className="text-destructive ml-1.5 text-xs font-light whitespace-nowrap">
                  - {error}
                </span>
              )}
            </label>
          )}
        </div>
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & { error?: string; touched?: boolean }
>(({ className, error, touched, onChange, ...props }, ref) => (
  <input
    className={cn(
      'flex w-full rounded-r-[0.625rem] bg-white px-4 py-4 text-lg outline-none placeholder:text-transparent autofill:shadow-[0_0_0_62.5rem_var(--background)_inset] disabled:cursor-not-allowed disabled:opacity-50 [&-webkit-autofill]:transition-colors [&-webkit-autofill]:duration-[5000s] [&-webkit-autofill]:[-webkit-text-fill-color:var(--foreground)]',
      className,
    )}
    inputMode="numeric"
    onBeforeInput={(e) => {
      const { data } = e.nativeEvent;
      if (data && !/^\d*$/.test(data)) {
        e.preventDefault();
      }
    }}
    onPaste={(e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
      document.execCommand('insertText', false, pasted);
    }}
    {...props}
    onChange={(e) => {
      const sanitized = e.target.value.replace(/\D/g, '');
      if (sanitized !== e.target.value) {
        e.target.value = sanitized;
      }
      onChange?.(e);
    }}
    ref={ref}
  />
));
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: Array<CountryEntry>;
  onChange: (country: RPNInput.Country) => void;
  // We might receive error/touched here if RPNInput passes them down
  error?: string;
  touched?: boolean;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const safeCountry = selectedCountry;

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open: boolean) => {
        setIsOpen(open);
        open && setSearchValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'flex shrink-0 items-center gap-2 rounded-s-[0.625rem] rounded-e-none px-3 py-4 hover:bg-transparent focus:z-10 focus:ring-0',
            'h-full min-w-25',
          )}
          disabled={disabled}
        >
          <FlagComponent country={safeCountry} countryName={safeCountry} />
          <span className="text-lg">
            {`+${RPNInput.getCountryCallingCode(safeCountry)}`}
          </span>
          <ChevronsUpDown
            className={cn(
              'size-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100',
            )}
          />
          <div className="bg-input h-8 w-px" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-75 p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={(value: string) => {
              setSearchValue(value);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]',
                  );
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder="Search country..."
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={safeCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps {
  country: RPNInput.Country;
  countryName: string;
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={cn(
          'ml-auto size-4',
          country === selectedCountry ? 'opacity-100' : 'opacity-0',
        )}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneInput };
